import { useInvestmentAccountDetail, type InvestmentHolding } from '@egw/api'
import { GenericError, Link, PageHeader } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { Currency, getCurrencySymbol, getFormattedBalanceForOverview } from '@egw/utils'
import { type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useParams } from 'react-router'

export const CompanionAccountHoldings: FC = () => {
  const { t } = useTranslation('features-holdings')

  const { alias = '' } = useParams<{
    alias: string
  }>()

  const {
    data: investmentAccountDetail,
    isLoading,
    isError,
  } = useInvestmentAccountDetail(parseInt(alias), {
    enabled: Boolean(alias),
  })

  const formattedTotalBalance = investmentAccountDetail
    ? getFormattedBalanceForOverview(investmentAccountDetail.balance.accountValue)
    : undefined

  return (
    <>
      {isError || investmentAccountDetail === undefined ? (
        <div className="egw-container">
          <GenericError errorMessage={t('holdings.overview_fatal_error')} />
        </div>
      ) : null}
      {isLoading ? (
        <div className="egw-container">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-xl" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {!isLoading && investmentAccountDetail ? (
        <PageHeader
          title={t('holdings.holdings')}
          subtitle={investmentAccountDetail.accountName}
          backLinkUrl={WEALTH_ROUTES.INVESTMENTS_COMPANION_ACCOUNT_HOLDINGS.backPath?.replace(':alias', alias)}
        />
      ) : null}

      {investmentAccountDetail && 'holdings' in investmentAccountDetail ? (
        <div className="egw-container pt-0">
          <div className="flex flex-col gap-md">
            <div>
              <span
                data-testid="overview-customer-networth"
                className="mb-2xs typo-primary-3xl-light"
                id="total_balance"
              >
                <span
                  data-testid="overview-networth-currency"
                  className="text-text-meta typo-primary-xl-light relative bottom-0 md:bottom-[.625rem]"
                >
                  {getCurrencySymbol(String(investmentAccountDetail.balance.accountValue.currencyCode))}
                </span>
                <span
                  data-testid="overview-networth-whole"
                  className="text-text-header left-2xl text-right typo-primary-3xl-semibold"
                >
                  {formattedTotalBalance?.integers}
                </span>
                <span data-testid="overview-networth-decimal" className="text-text-meta typo-primary-3xl-light">
                  {formattedTotalBalance?.cents}
                </span>
              </span>
              <div>
                <div className="mt-lg flex flex-col items-start justify-start">
                  {
                    // map over categories
                    investmentAccountDetail.holdings.map(({ name, symbol, amount }: InvestmentHolding) => {
                      return (
                        <Link
                          href={WEALTH_ROUTES.INVESTMENTS_COMPANION_ACCOUNT_HOLDINGS_DETAILS.path
                            .replace(':alias', alias.toString())
                            .replace(':symbol', symbol)}
                          key={symbol}
                          className="flex w-full flex-row justify-between border-light last-of-type:border-b-0 border-b py-md"
                        >
                          <div className="flex flex-row gap-md">
                            {/* name */}
                            <div className="flex flex-col items-start">
                              <p className="text-text-body typo-primary-base">{name}</p>
                              <p className="text-text-meta typo-primary-sm">{symbol}</p>
                            </div>
                          </div>
                          {/* money */}
                          <div className="flex flex-row justify-between ">
                            <div className="flex flex-row">
                              <div className="text-text-header typo-primary-base-light">
                                {amount.currencyCode ? getCurrencySymbol(amount.currencyCode) : null}
                              </div>
                              <span className="text-text-header typo-primary-base-semibold">
                                {Currency.integerCents.onlyMinus(amount.amount)}
                              </span>
                            </div>
                          </div>
                        </Link>
                      )
                    })
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
